import * as React from "react";
import { useDialogState } from "hooks/useDialogState";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import { LabelValue } from "components/LabelValue/LabelValue";
import { UploadImageToClientConfigDialog } from "../../../components/UploadImageToClientConfigDialog";
import { ClientConfig, ClientDataType, ClientsService } from "gen/clients";
import { useMutation } from "react-query";
import { Stack } from "@mui/material";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";

interface Props {
  clientConfig: ClientConfig;
  disabled?: boolean;
  onSaveSuccess: () => void;
}

const CONTENT_ROOT_URL = import.meta.env.VITE_STATIC_CONTENT_ROOT_URL;

const LogoField: React.FC<Props> = ({ clientConfig, disabled, onSaveSuccess }) => {
  const [isLogoUploadDialogOpen, openLogoUploadDialog, closeLogoUploadDialog] = useDialogState();

  const {
    mutate,
    error: mutateError,
    isLoading
  } = useMutation("updateClientConfig", {
    onSuccess: onSaveSuccess,
    mutationFn: ClientsService.updateClientConfig
  });

  const onDeleteLogoClick = React.useCallback(() => {
    const clientConfigUpdate = {
      ...clientConfig,
      logoKey: undefined
    };
    mutate({ id: clientConfigUpdate.id, requestBody: clientConfigUpdate });
  }, [clientConfig, mutate]);

  return (
    <>
      <LabelValue
        label="Logo"
        value={
          <div>
            {!clientConfig.logoKey && (
              <Button size="small" variant="outlined" onClick={openLogoUploadDialog} disabled={disabled || isLoading}>
                Add
              </Button>
            )}
            {clientConfig.logoKey && (
              <Stack spacing={1}>
                <span>
                  <Link href={`${CONTENT_ROOT_URL}/${clientConfig.logoKey}`} target="_blank">
                    {clientConfig.logoKey?.substring(clientConfig.logoKey.lastIndexOf("/") + 1)}
                  </Link>
                  <Button
                    onClick={onDeleteLogoClick}
                    size="small"
                    variant="outlined"
                    disabled={disabled || isLoading}
                    sx={{ ml: 1 }}
                  >
                    Delete
                  </Button>
                </span>
                {mutateError && <ApiErrorMessage apiError={mutateError} />}
              </Stack>
            )}
          </div>
        }
      />
      {isLogoUploadDialogOpen && (
        <UploadImageToClientConfigDialog
          clientConfig={clientConfig}
          dataType={ClientDataType.LOGO}
          onSuccess={() => {
            closeLogoUploadDialog();
            onSaveSuccess();
          }}
          onClose={closeLogoUploadDialog}
        />
      )}
    </>
  );
};

export { LogoField };
